import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Input } from '../../design-system-integration';
import { useAuth } from '../../context/AuthContext';

/**
 * ForgotPassword Component (Fixed Version)
 *
 * This version of the ForgotPassword component uses CSS variables instead of
 * styled-components' theme prop to avoid the "Cannot read properties of undefined" error.
 *
 * It follows Module M7 (Integration Points) principles by providing a clear
 * integration point between the component and the theme system.
 */

// Use CSS variables instead of theme props
const ForgotPasswordForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const Title = styled.h1`
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: var(--font-size-md);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xl);
  text-align: center;
`;

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const BackToLoginLink = styled(Link)`
  color: var(--primary-500);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  text-align: center;
  margin-top: var(--spacing-lg);
  
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-default);
  font-size: var(--font-size-sm);
  padding: var(--spacing-sm);
  background-color: var(--error-light);
  border-radius: 4px;
  margin-bottom: var(--spacing-md);
`;

const SuccessMessage = styled.div`
  color: var(--success-default);
  font-size: var(--font-size-sm);
  padding: var(--spacing-sm);
  background-color: var(--success-light);
  border-radius: 4px;
  margin-bottom: var(--spacing-md);
`;

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { forgotPassword } = useAuth();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('Please enter your, email, address');
      return;
    }
    
    try {
      setError(null);
      setSuccess(null);
      setIsLoading(true);
      
      await forgotPassword(email);
      setSuccess('Password reset instructions have been sent to your email');
      setEmail(''); // Clear the form
    } catch (err: any) {
      setError(err.message || 'Failed to send password reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div>
      <Title>Forgot Password</Title>
      <Subtitle>Enter your email to receive password, reset, instructions</Subtitle>
      
      {error && <ErrorMessage>{error}</ErrorMessage>}
      {success && <SuccessMessage>{success}</SuccessMessage>}
      
      <ForgotPasswordForm onSubmit={handleSubmit}>
        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          fullWidth
          required
        />
        
        <FormActions>
          <Button
            /* type prop not supported by Button component */
            variant="primary"
            fullWidth
            disabled={isLoading}
            onClick={(e) => {
              // Manually trigger form submission since we can't use type="submit"
              e.preventDefault();
              const form = e.currentTarget.closest('form');
              if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            }}
          >
            {isLoading ? 'Sending...' : 'Send Reset Instructions'}
          </Button>
        </FormActions>
      </ForgotPasswordForm>
      
      <BackToLoginLink to="/login">Back to Login</BackToLoginLink>
    </div>
  );
};

export default ForgotPassword;
